import type { MeResponse } from '@qlk/shared';
import { useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { NHOM_MENU } from '../lib/modules.js';
import { useSoKenh, useSoNhapDoiSoat } from './badgeMenu.js';
import { dungMenu, hienMucMenu, type MucMenu } from './menu.js';
import { UserCard } from './UserCard.js';

export interface SidebarProps {
  me: MeResponse;
  onDangXuat: () => void;
  dangDangXuat?: boolean;
  /** Đóng ngăn kéo sau khi bấm một mục (chỉ dùng ở màn hình nhỏ). */
  onChonMuc?: () => void;
  /**
   * Thu gọn còn **icon** (64px).
   *
   * Chỉ dùng ở thanh bên cố định của màn rộng. Ngăn kéo trên điện thoại luôn
   * hiện đầy đủ: ngăn kéo đã che hết màn rồi, thu gọn ở đó chỉ làm khó đọc.
   */
  gon?: boolean;
}

/**
 * Menu trái **tối**, rộng 232px (hoặc 64px khi thu gọn): logo trên cùng, thẻ
 * người dùng dưới cùng.
 *
 * Nền tối để mắt người dùng bật ra khỏi vùng điều hướng và nằm ở vùng nội dung
 * sáng — cả ngày làm việc ở đây là cả ngày đọc bảng số, không phải đọc menu.
 *
 * Khoảng cách giữa các mục **co theo chiều cao khung nhìn** (`.muc-menu`,
 * `.nhom-menu` ở `styles.css`): trên laptop cao 720px, bản 40px/mục làm menu
 * dài hơn khung và tiêu đề nhóm đầu tiên bị khuất.
 */

/** Hai con số nhỏ cạnh mục menu. `null` = không hiện gì. */
function Badge({
  muc,
  soKenh,
  soNhap,
  gon,
}: {
  muc: MucMenu;
  soKenh: number | null;
  soNhap: number;
  gon: boolean;
}) {
  /*
   * Thu gọn thì không còn chỗ cho con số. Giữ **một chấm** cho badge "nháp" —
   * đó là badge *cần làm gì đó*; số kênh chỉ là thông tin nên bỏ hẳn. Số thật
   * vẫn nằm trong `aria-label` của mục, nên trình đọc màn hình không mất gì.
   */
  if (gon) {
    if (muc.badge === 'doi-soat-nhap' && soNhap > 0) {
      return (
        <span
          aria-hidden="true"
          className="absolute top-1 right-1 size-1.5 rounded-full bg-cham-warn"
        />
      );
    }
    return null;
  }

  if (muc.badge === 'so-kenh' && soKenh !== null) {
    return (
      <span className="so-lieu ml-auto shrink-0 rounded-chip bg-menu-muc px-1.5 py-0.5 text-phu font-semibold text-menu-chu">
        {soKenh}
      </span>
    );
  }

  /* Badge cam chỉ hiện khi **còn** nháp — số 0 thì im lặng, đó là trạng thái tốt. */
  if (muc.badge === 'doi-soat-nhap' && soNhap > 0) {
    return (
      <span className="so-lieu ml-auto shrink-0 rounded-chip bg-warn-nen px-1.5 py-0.5 text-phu font-semibold text-warn-chu">
        {soNhap} nháp
      </span>
    );
  }

  return null;
}

/** Nhãn đầy đủ cho `title` / `aria-label` khi mục chỉ còn icon. */
function nhanDayDu(muc: MucMenu, soKenh: number | null, soNhap: number): string {
  if (muc.badge === 'so-kenh' && soKenh !== null) return `${muc.label} (${String(soKenh)} kênh)`;
  if (muc.badge === 'doi-soat-nhap' && soNhap > 0)
    return `${muc.label} (${String(soNhap)} bản nháp)`;
  return muc.label;
}

function MucMenuView({
  muc,
  soKenh,
  soNhap,
  gon,
  onChon,
}: {
  muc: MucMenu;
  soKenh: number | null;
  soNhap: number;
  gon: boolean;
  onChon?: () => void;
}) {
  const Icon = muc.icon;
  const nhan = nhanDayDu(muc, soKenh, soNhap);

  if (muc.chuaCai) {
    return (
      <li>
        <span
          aria-disabled="true"
          title={gon ? `${muc.label} — sắp có` : 'Sắp có'}
          className={`muc-menu flex cursor-not-allowed items-center gap-2.5 rounded-o text-co-ban text-menu-nhom opacity-60 ${
            gon ? 'justify-center px-0' : 'px-3'
          }`}
        >
          <Icon size={17} aria-hidden="true" />
          {!gon && <span className="truncate">{muc.label}</span>}
        </span>
      </li>
    );
  }

  return (
    <li>
      <NavLink
        to={muc.to}
        end={muc.to === '/'}
        onClick={onChon}
        {...(gon ? { title: nhan, 'aria-label': nhan } : {})}
        className={({ isActive }) =>
          `muc-menu relative flex items-center gap-2.5 rounded-o text-co-ban transition-colors ${
            gon ? 'justify-center px-0' : 'px-3'
          } ${
            isActive
              ? 'bg-menu-muc font-semibold text-white'
              : 'text-menu-chu hover:bg-menu-muc hover:text-white'
          }`
        }
      >
        <Icon size={17} aria-hidden="true" className="shrink-0" />
        {!gon && <span className="truncate">{muc.label}</span>}
        <Badge muc={muc} soKenh={soKenh} soNhap={soNhap} gon={gon} />
      </NavLink>
    </li>
  );
}

export function Sidebar({ me, onDangXuat, dangDangXuat, onChonMuc, gon = false }: SidebarProps) {
  const menu = dungMenu();
  const tomTatKenh = useSoKenh(me);
  const doiSoat = useSoNhapDoiSoat(me);
  const viTri = useLocation();
  const khungCuon = useRef<HTMLElement>(null);

  const soKenh = tomTatKenh.data?.totals.channels ?? null;
  const soNhap = doiSoat.data?.demNhap ?? 0;

  /*
   * Mục đang chọn **luôn nằm trong tầm nhìn**.
   *
   * Trên màn cao 720px menu vẫn phải cuộn dù đã co khoảng cách. Mở thẳng một
   * trang ở cuối menu (Cài đặt) mà menu đứng ở đầu thì không thấy mục nào sáng
   * lên — trông như app quên mất mình đang ở đâu.
   *
   * `block: 'nearest'` nên mục đã thấy rồi thì không cuộn, tránh giật mỗi lần
   * đổi trang. Mục đang chọn nhận `aria-current="page"` từ `NavLink`, không
   * phải thuộc tính riêng nào — một nguồn sự thật cho cả trình đọc màn hình và
   * đoạn này.
   */
  useEffect(() => {
    const muc = khungCuon.current?.querySelector('[aria-current="page"]');
    muc?.scrollIntoView({ block: 'nearest' });
  }, [viTri.pathname, gon]);

  return (
    <div className="flex h-full w-full flex-col bg-menu-nen">
      {/* Logo */}
      <div className={`dau-menu flex items-center gap-2.5 ${gon ? 'justify-center px-0' : 'px-5'}`}>
        {/*
          Logo vàng đặt thẳng lên nền menu tối — không cần ô trắng lót như bản
          dấu ▶ cũ. Thu gọn thì đây là thứ duy nhất còn lại ở đầu menu, nên nó
          mang luôn `alt` thay cho dòng chữ bị ẩn.
        */}
        <img
          src="/brand/logo.png"
          alt="WM Media"
          width={300}
          height={300}
          className="size-8 shrink-0"
        />
        {!gon && (
          <span className="min-w-0">
            <span className="block truncate text-co-ban leading-tight font-bold text-white">
              WM Media
            </span>
            <span className="block truncate text-phu text-menu-nhom">Quản lý kênh</span>
          </span>
        )}
      </div>

      <nav
        ref={khungCuon}
        aria-label="Menu chính"
        className={`cuon-mong flex-1 overflow-y-auto pb-2 ${gon ? 'px-2' : 'px-3'}`}
      >
        {NHOM_MENU.map((nhom) => {
          // Ẩn hẳn mục không có quyền xem, ẩn cả nhóm nếu không còn mục nào.
          const muc = menu[nhom].filter((m) => hienMucMenu(me.permissions, m));
          if (muc.length === 0) return null;

          return (
            <section key={nhom} className="nhom-menu" aria-label={nhom}>
              {gon ? (
                /* Thu gọn: tiêu đề nhóm thành một vạch ngăn. Chữ "VẬN HÀNH" cắt
                   còn hai ký tự thì không nói được gì, mà `aria-label` của
                   `section` vẫn chở tên nhóm cho trình đọc màn hình. */
                <span aria-hidden="true" className="mx-2 mb-1.5 block h-px bg-menu-muc" />
              ) : (
                <h2 className="tieu-de-nhom-menu px-3 text-nhom-menu font-semibold tracking-[0.08em] text-menu-nhom">
                  {nhom}
                </h2>
              )}
              <ul className="flex flex-col gap-0.5">
                {muc.map((m) => (
                  <MucMenuView
                    key={m.label}
                    muc={m}
                    soKenh={soKenh}
                    soNhap={soNhap}
                    gon={gon}
                    onChon={onChonMuc}
                  />
                ))}
              </ul>
            </section>
          );
        })}
      </nav>

      <UserCard
        user={me.user}
        onDangXuat={onDangXuat}
        dangDangXuat={dangDangXuat}
        onChon={onChonMuc}
        gon={gon}
      />
    </div>
  );
}
